import React from 'react';
import { useNudges, useDismissNudge } from '../../hooks/useAnalytics';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { StatusBadge, ErrorState, EmptyState } from './AnalyticsShared';
import { 
  AlertCircle, 
  TrendingDown, 
  Clock, 
  Target, 
  DollarSign,
  X,
  CheckCircle
} from 'lucide-react';

export function AlertsTab({ range }) {
  const { data, isLoading, error, refetch } = useNudges(range);
  const dismissMutation = useDismissNudge();

  const handleDismiss = async (key) => {
    try {
      await dismissMutation.mutateAsync({ key, durationDays: 30 });
    } catch (err) {
      console.error('Failed to dismiss nudge:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="animate-pulse space-y-4">
                <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  if (!data || !data.nudges || data.nudges.length === 0) {
    return (
      <Card className="bg-green-50 border-green-200">
        <CardContent className="p-12 text-center">
          <div className="mx-auto max-w-md">
            <div className="mb-4 text-green-600">
              <CheckCircle className="mx-auto h-16 w-16" />
            </div>
            <h3 className="text-lg font-semibold text-green-900 mb-2">All Clear!</h3>
            <p className="text-sm text-green-800">
              You have no active alerts. Your listings and campaigns are performing well. Keep up the great work!
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getNudgeIcon = (type) => {
    switch (type) {
      case 'LOW_CTR':
        return <TrendingDown className="h-5 w-5" />;
      case 'SLOW_REPLIES':
        return <Clock className="h-5 w-5" />;
      case 'AD_LOST_SHARE_BUDGET':
      case 'AD_LOST_SHARE_RANK':
      case 'AD_OPPORTUNITY_LOST_BUDGET':
        return <DollarSign className="h-5 w-5" />;
      case 'AD_INEFFICIENT':
      case 'AD_LOW_COVERAGE':
        return <Target className="h-5 w-5" />;
      default:
        return <AlertCircle className="h-5 w-5" />;
    }
  };

  const getNudgeColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'border-red-200 bg-red-50';
      case 'medium':
        return 'border-orange-200 bg-orange-50';
      case 'low':
        return 'border-yellow-200 bg-yellow-50';
      default:
        return 'border-gray-200 bg-gray-50';
    }
  };

  const getNudgeTitle = (type) => {
    const titles = {
      LOW_CTR: 'Low Click-Through Rate',
      SLOW_REPLIES: 'Slow Response Time',
      AD_LOST_SHARE_BUDGET: 'Ad Budget Limitation',
      AD_LOST_SHARE_RANK: 'Ad Rank Issue',
      AD_OPPORTUNITY_LOST_BUDGET: 'Missed Ad Opportunities',
      AD_INEFFICIENT: 'Inefficient Ad Spending',
      AD_LOW_COVERAGE: 'Limited Ad Coverage',
      ON_TIME_RISK: 'On-Time Delivery Risk',
      DISPUTE_SPIKE: 'Dispute Rate Increase',
    };
    return titles[type] || type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <div className="space-y-6">
      {/* Summary */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {data.nudges.length} Active Alert{data.nudges.length !== 1 ? 's' : ''}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Review and address these items to improve your performance
              </p>
            </div>
            <AlertCircle className="h-8 w-8 text-orange-500" />
          </div>
        </CardContent>
      </Card>

      {/* Nudges/Alerts List */}
      <div className="space-y-4">
        {data.nudges.map((nudge, index) => (
          <Card key={index} className={`${getNudgeColor(nudge.severity)}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    nudge.severity === 'high' ? 'bg-red-100 text-red-700' :
                    nudge.severity === 'medium' ? 'bg-orange-100 text-orange-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {getNudgeIcon(nudge.type)}
                  </div>
                  <div>
                    <CardTitle className="text-lg">{getNudgeTitle(nudge.type)}</CardTitle>
                    {nudge.listing_title && (
                      <p className="text-sm text-gray-600 mt-1">
                        Listing: {nudge.listing_title}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge type={nudge.type}>
                    {nudge.severity === 'high' ? 'Urgent' : nudge.severity === 'medium' ? 'Important' : 'Notice'}
                  </StatusBadge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDismiss(nudge.key)}
                    disabled={dismissMutation.isLoading}
                    className="gap-1"
                  >
                    <X className="h-4 w-4" />
                    Dismiss
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Message */}
              <p className="text-sm text-gray-700">{nudge.message}</p>

              {/* Metrics */}
              {nudge.metrics && Object.keys(nudge.metrics).length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-white rounded-lg border border-gray-200">
                  {Object.entries(nudge.metrics).map(([key, value]) => (
                    <div key={key}>
                      <p className="text-xs text-gray-600 capitalize">
                        {key.replace(/_/g, ' ')}
                      </p>
                      <p className="text-lg font-semibold text-gray-900">
                        {typeof value === 'number' ? 
                          (key.includes('rate') || key.includes('ctr') || key.includes('share') ? 
                            `${(value * 100).toFixed(1)}%` : 
                            key.includes('cents') || key.includes('spend') || key.includes('budget') ?
                              `$${(value / 100).toFixed(2)}` :
                              value.toLocaleString()
                          ) : value}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* Action Items */}
              {nudge.action && (
                <div className="p-4 bg-white rounded-lg border-l-4 border-blue-500">
                  <p className="text-sm font-medium text-gray-900 mb-1">Recommended Action:</p>
                  <p className="text-sm text-gray-700">{nudge.action}</p>
                </div>
              )}

              {/* Reference Links */}
              {nudge.listing_id && (
                <div className="text-sm text-gray-600">
                  <a 
                    href={`/listing/${nudge.listing_id}`} 
                    className="text-blue-600 hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Listing →
                  </a>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Dismiss Info */}
      <Card className="bg-gray-50">
        <CardContent className="p-4">
          <p className="text-xs text-gray-600">
            <strong>Note:</strong> Dismissed alerts will be hidden for 30 days. 
            If the issue persists, the alert will reappear after this period.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
